import React from 'react';
import {
  Alert,
  Box,
  Button,
  Divider,
  Modal,
  ModalDialog,
  Table,
  Typography,
} from '@mui/joy';
import { PaperPlaneTilt as SendIcon } from '@phosphor-icons/react';

import { SRS_CHECKOUT_DISCLAIMER } from './srsOrdering';

export interface OrderSummary {
  isSrs?: boolean;
  mockMode?: boolean;
  distributorName: string;
  lineCount: number;
  pricedSubtotal: number;
  unpricedZeroCount: number;
  shipToLabel: string;
  branchLabel: string;
  deliveryLabel: string;
  requestedDate?: string;
  poNumber: string;
  jobLabel?: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  submitting: boolean;
  summary: OrderSummary;
}

/** Final review before a live order goes to the distributor. */
export default function SubmitOrderConfirmDialog({ open, onClose, onConfirm, submitting, summary }: Props) {
  return (
    <Modal open={open} onClose={submitting ? undefined : onClose}>
      <ModalDialog data-sentry-block role="alertdialog" sx={{ minWidth: 380 }}>
        <Typography level="title-lg">{summary.mockMode ? 'Submit simulated order?' : `Submit order to ${summary.distributorName}?`}</Typography>
        <Typography level="body-sm" color="neutral">
          {summary.mockMode ? 'Demo mode: this submits a simulated order. No order is sent to the distributor.' : 'This places a live order with the branch — it can only be changed by contacting them afterwards.'}
        </Typography>

        <Table size="sm" sx={{ my: 1 }}>
          <tbody>
            <tr><td><Typography level="body-sm" color="neutral">Lines</Typography></td><td>{summary.lineCount}</td></tr>
            <tr><td><Typography level="body-sm" color="neutral">Priced subtotal</Typography></td><td>${summary.pricedSubtotal.toFixed(2)}</td></tr>
            <tr><td><Typography level="body-sm" color="neutral">Ship-To</Typography></td><td>{summary.shipToLabel}</td></tr>
            <tr><td><Typography level="body-sm" color="neutral">Branch</Typography></td><td>{summary.branchLabel}</td></tr>
            <tr><td><Typography level="body-sm" color="neutral">Fulfillment</Typography></td><td>{summary.deliveryLabel}</td></tr>
            {summary.requestedDate && (
              <tr><td><Typography level="body-sm" color="neutral">Requested date</Typography></td><td>{summary.requestedDate}</td></tr>
            )}
            <tr><td><Typography level="body-sm" color="neutral">PO number</Typography></td><td>{summary.poNumber}</td></tr>
            {summary.jobLabel && (
              <tr><td><Typography level="body-sm" color="neutral">Job</Typography></td><td>{summary.jobLabel}</td></tr>
            )}
          </tbody>
        </Table>

        {summary.unpricedZeroCount > 0 && (
          <Alert color="primary" variant="soft">
            {summary.unpricedZeroCount} line{summary.unpricedZeroCount === 1 ? '' : 's'} show $0.00 —
            the branch will confirm pricing when processing the order.
          </Alert>
        )}

        {summary.isSrs && <Alert color="neutral">{SRS_CHECKOUT_DISCLAIMER}</Alert>}
        <Divider sx={{ my: 1 }} />
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          <Button variant="plain" color="neutral" onClick={onClose} disabled={submitting}>
            Keep editing
          </Button>
          <Button startDecorator={<SendIcon />} loading={submitting} onClick={onConfirm}>
            {summary.mockMode ? 'Submit simulated order' : 'Submit to distributor'}
          </Button>
        </Box>
      </ModalDialog>
    </Modal>
  );
}
